import 'react-mde/lib/styles/css/react-mde-all.css';
import { BASE_URL } from '@synergy/config';
import { Fragment, ReactElement, ReactNode } from 'react';
import { Navigate, RouterProvider, createBrowserRouter, useLocation } from 'react-router-dom';

import { PaloAppLayout } from './palo-app-layout';
import { PaloFormPreview } from './features/palo-preview/palo-form-preview';
import { PaloNotFound } from './common/components/palo/palo-not-found';
import { PaloResourceGuard } from './common/components/palo/palo-resource-guard';
import { ROUTES } from './features/sidebar-menu/navigation-drawer/routes';
import { protectedComponents as pc } from './protected-components';

/**
 * A document opened from the chain of another starts on its overview: the
 * mask of the same kind stayed mounted and kept the tab it was on (#2323).
 * Arrows and lists open without the mark and keep the tab.
 */
const FreshWhenOpened = ({ children }: { children: ReactNode }): ReactElement => {
    const opened = (useLocation().state as { opened?: number } | null)?.opened;

    return <Fragment key={opened ?? 'kept'}>{children}</Fragment>;
};

/** A mask whose record may be missing, with the way back to its own list. */
const guarded = (element: ReactNode, title: string, backTo: string, backLabel: string): ReactNode => (
    <PaloResourceGuard title={title} backTo={backTo} backLabel={backLabel}>
        <FreshWhenOpened>{element}</FreshWhenOpened>
    </PaloResourceGuard>
);

const router = createBrowserRouter(
    [
        {
            element: <PaloAppLayout />,
            children: [
                // The address without a path is the way in, not a wrong turn.
                { index: true, element: <Navigate to={ROUTES.dashboard} replace={true} /> },
                { path: ROUTES.dashboard + '/*', element: <pc.Dashboard /> },
                { path: ROUTES.demands, element: <pc.Demands /> },
                {
                    path: ROUTES.demandsDetail,
                    element: guarded(
                        <pc.DemandDetails />,
                        'Diese Bedarfserhebung gibt es nicht.',
                        ROUTES.demands,
                        'Zur Bedarfsliste'
                    ),
                },
                { path: ROUTES.inquiries, element: <pc.Inquiries /> },
                {
                    path: ROUTES.inquiriesDetail,
                    element: guarded(
                        <pc.InquiryDetails />,
                        'Diese Anfrage gibt es nicht.',
                        ROUTES.inquiries,
                        'Zur Anfragenliste'
                    ),
                },
                { path: ROUTES.purchaseOrders, element: <pc.PurchaseOrders /> },
                {
                    path: ROUTES.purchaseOrdersDetail,
                    element: guarded(
                        <pc.PurchaseOrderDetails />,
                        'Diese Bestellung gibt es nicht.',
                        ROUTES.purchaseOrders,
                        'Zur Bestellliste'
                    ),
                },
                { path: ROUTES.salesOrders, element: <pc.SalesOrders /> },
                {
                    path: ROUTES.salesOrdersDetail,
                    element: guarded(
                        <pc.SalesOrderDetails />,
                        'Diesen Auftrag gibt es nicht.',
                        ROUTES.salesOrders,
                        'Zur Liste'
                    ),
                },
                { path: ROUTES.invoices.list, element: <pc.Invoices /> },
                {
                    path: ROUTES.invoices.detail,
                    element: guarded(
                        <pc.InvoiceDetails />,
                        'Diese Rechnung gibt es nicht.',
                        ROUTES.invoices.list,
                        'Zur Rechnungsliste'
                    ),
                },
                { path: ROUTES.contactsAndOrganizations.suppliers.list, element: <pc.Suppliers /> },
                { path: ROUTES.contactsAndOrganizations.suppliers.detail + '/*', element: <pc.SupplierDetails /> },
                { path: ROUTES.contactsAndOrganizations.customers.list, element: <pc.Customers /> },
                { path: ROUTES.contactsAndOrganizations.customers.detail + '/*', element: <pc.CustomerDetails /> },
                { path: ROUTES.contactsAndOrganizations.contacts.list, element: <pc.Contacts /> },
                { path: ROUTES.contactsAndOrganizations.contacts.detail + '/*', element: <pc.ContactDetails /> },
                { path: ROUTES.configuration.data + '/*', element: <pc.ConfigurationData /> },
                { path: ROUTES.configuration.search + '/*', element: <pc.ConfigurationSearch /> },
                { path: ROUTES.configuration.finance + '/*', element: <pc.ConfigurationFinance /> },
                { path: ROUTES.configuration.organization + '/*', element: <pc.ConfigurationOrganization /> },
                { path: ROUTES.products.import.overview.base + '/*', element: <pc.Imports /> },
                { path: ROUTES.products.import.detail + '/*', element: <pc.ImportDetails /> },
                { path: ROUTES.products.mapping.base + '/*', element: <pc.Mappings /> },
                { path: ROUTES.products.product.base, element: <pc.Products /> },
                {
                    path: ROUTES.products.product.detail + '/*',
                    element: guarded(
                        <pc.ProductDetails />,
                        'Diesen Artikel gibt es nicht.',
                        ROUTES.products.product.base,
                        'Zur Artikelliste'
                    ),
                },
                { path: ROUTES.contentManagement.headerBanner.list, element: <pc.HeaderBanner /> },
                { path: ROUTES.contentManagement.headerBanner.detail, element: <pc.HeaderBannerDetails /> },
                { path: ROUTES.contentManagement.poolNews.list, element: <pc.NewsPost /> },
                { path: ROUTES.contentManagement.poolNews.detail, element: <pc.NewsPostDetails /> },
                { path: ROUTES.contentManagement.staticSites.list, element: <pc.StaticSites /> },
                { path: ROUTES.contentManagement.staticSites.detail, element: <pc.StaticSitesDetails /> },
                { path: ROUTES.contentManagement.footer, element: <pc.FooterSetting /> },
                { path: ROUTES.contentManagement.supplierSpecialOffers.list, element: <pc.SupplierSpecialOffers /> },
                {
                    path: ROUTES.contentManagement.supplierSpecialOffers.detail,
                    element: <pc.SupplierSpecialOfferDetails />,
                },
                { path: ROUTES.contentManagement.sponsorBanner.list, element: <pc.SponsorBanner /> },
                { path: ROUTES.contentManagement.sponsorBanner.detail, element: <pc.SponsorBannerDetails /> },
                { path: ROUTES.settings + '/*', element: <pc.RestrictedAppSettings /> },
                { path: ROUTES.bonusSettlements.overview, element: <pc.BonusSettlementsRestricted /> },
                {
                    path: ROUTES.bonusSettlements.supplierDetail,
                    element: <pc.BonusSettlementSupplierDetailRestricted />,
                },
                {
                    path: ROUTES.bonusSettlements.customerDetail,
                    element: <pc.BonusSettlementCustomerDetailRestricted />,
                },
                { path: ROUTES.bonusSettlements.detail.base + '/*', element: <pc.BonusSettlementOverviewRestricted /> },
                { path: 'palo-form-preview', element: <PaloFormPreview /> },
                // Inside the layout on purpose: a wrong address keeps the
                // navigation, so the way on is where it always is.
                {
                    path: '*',
                    element: (
                        <PaloNotFound
                            title="Diese Seite gibt es nicht."
                            backTo={ROUTES.dashboard}
                            backLabel="Zum Cockpit"
                        />
                    ),
                },
            ],
        },
    ],
    { basename: BASE_URL || '/' }
);

export const AppRouter = () => <RouterProvider router={router} />;
